import * as THREE from "three";
import type { WeatherColumn } from "@hyperlight/shared";
import { ENVIRONMENT } from "../../visual/styleProfiles";

/** Humid low-sun mornings/evenings carry more ground mist; dry climates and
 * daylight-free scenes don't acquire a constant bright fog layer. */
export function weatherMistAmount(mist: number, wet: number, day: number, sunElevation: number): number {
  // Use the actual radial/light dot: the sky's day factor already reaches 1
  // before the sun is high. An eclipse alone must not create morning humidity.
  const lowSun = (1 - THREE.MathUtils.smoothstep(sunElevation, .18, .65))
    * THREE.MathUtils.smoothstep(sunElevation, -.04, .06) * THREE.MathUtils.clamp(day * 3, 0, 1);
  return Math.min(1, mist + wet * .22 + ENVIRONMENT.morningMist * lowSun * Math.min(1, mist / .3));
}

/** S07 distance response, alongside subtler terrain-aware local mist. Keep
 * the approved day/night balance and the sky's horizon colour as endpoint. */
export function landscapeHazeDensity(presence:number,skyDay:number,cloud:number,precip:number,volume:boolean):number {
  const p=THREE.MathUtils.clamp(presence,0,1),d=THREE.MathUtils.clamp(skyDay,0,1);
  const day=.04+.96*d,weather=THREE.MathUtils.clamp(cloud*.4+precip*.6,0,1);
  return p*2.2e-4*(.55+.45*day)*ENVIRONMENT.haze*(1+weather*(volume?.55:2)*p);
}

/** Common raw sky tint, before day/night exposure. Never introduces a fixed
 * bright grey at night, nor changes an alien atmosphere to an Earth-blue hue. */
export function weatherSkyTint(color: THREE.Color, weather: WeatherColumn): void {
  const depth = weather.cloudDepth ?? weather.cloud * 2;
  const k = (1 - Math.exp(-depth * 0.45)) * 0.72;
  const lum = color.r * 0.2126 + color.g * 0.7152 + color.b * 0.0722;
  const grey = lum * (0.78 - weather.precip * 0.12);
  color.r += (grey - color.r) * k;
  color.g += (grey - color.g) * k;
  color.b += (grey - color.b) * k;
}

/** Nocturnal indigo sky, retaining the planet's atmospheric hue. Only the
 * background uses this contrast: sunlight, terrain fill and dusk stay intact. */
export function nightSkyTint(color: THREE.Color, day: number): void {
  const night=1-THREE.MathUtils.smoothstep(day,0,.18);
  color.r*=1-night*.70;
  color.g*=1-night*.48;
  color.b*=1-night*.05;
}

/** Rain ends in the lower cloud layer; atmospheric presence alone is insufficient. */
export function precipitationAltitude(altitude: number, cloudBase: number, thickness: number): number {
  const t = THREE.MathUtils.clamp((altitude - cloudBase) / Math.max(1, thickness), 0, 1);
  return 1 - t * t * (3 - 2 * t);
}
